// ─── AutoChinBot · Pending orders (\"Vào lệnh chờ\") ──────────────
// Second half of the shared [Vào lệnh] entry. A pending order is created from
// the Telegram conversation (price → volume → maxOrders → side → hours) and
// stored in `telegram_order_proposals` with action='pending_order':
//
//   pending_new    → recorded, awaiting price validation vs the LIVE market
//   pending_armed  → price is valid, monitored tick-by-tick for a touch
//   executed / rejected / cancelled / failed / expired → terminal
//
// This controller runs in the SAME background browser context as task1–task6
// (a module-level singleton per (strategyId, accountId)), because the price
// feed and the task5 risk gate both live in the browser.
//
// Behaviour:
//   1) Validate price — BUY must be BELOW the current market, SELL ABOVE it.
//      Invalid → reject the proposal (Telegram gets the error).
//   2) Arm the proposal and watch the price feed tick-by-tick. When the price
//      touches the target, run the task5 RISK GATE:
//         · allowed → fire a MARKET order via the edge function (cTrader)
//         · blocked → PAUSE ALL pending orders until an admin resumes them
//   3) Expire pending orders past their “hiệu lực” (validity) window.
//
// ALL DB WRITES go through the edge function (service role); the browser only
// READS the pending rows, matching the existing RLS model.

import { supabase } from '@/lib/supabase';
import telegramBot from '@/lib/telegram-bot-client';
import { buildSessionKey } from '@/lib/session-key';
import { ctraderPriceBus } from '@/lib/ctrader-price-bus';
import type { AutoChinBotEngine } from '@/lib/auto-chin-bot-engine';
import type { AutoChinRiskController } from '@/lib/autochin-risk-controller';
import type { AutoChinLogInput } from '@/lib/autochin-system-log';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinPendingOrder {
  id: string;
  chat_id: string | null;
  telegram_account_id: number | null;
  account_id: string | null;
  symbol_id: number | null;
  symbol: string | null;
  trade_side: string | null;
  volume: number | null;
  status: string;
  signal_context: Record<string, unknown> | null;
  expires_at: string | null;
  error_message: string | null;
  created_at: string | null;
}

/** Same custom event the monitor listens to, so the pause state stays in sync. */
export const AUTOCHIN_PENDING_PAUSE_EVENT = 'autochin:pending-pause';

// ─── Constants ──────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;
const TOUCH_THROTTLE_MS = 250;
const ACTIVE_STATUSES = ['pending_new', 'pending_armed'];

// ─── Controller ─────────────────────────────────────────────────

export class AutoChinPendingController {
  private bot: AutoChinBotEngine;
  private risk: AutoChinRiskController;
  private userId: string;
  private strategyId: string;
  private accountId: string;

  private logListeners = new Set<(l: AutoChinLogInput) => void>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private unsubPrice: (() => void) | null = null;
  private started = false;
  private polling = false;
  private paused = false;
  private pauseReason = '';
  private pauseLoaded = false;
  private lastTouchCheck = 0;

  /** Armed pending orders keyed by proposal id. */
  private armed = new Map<string, AutoChinPendingOrder>();
  /** Rejected/cancelled ids already settled — never re-process. */
  private settled = new Set<string>();

  constructor(
    bot: AutoChinBotEngine,
    risk: AutoChinRiskController,
    userId: string,
    strategyId: string,
    accountId: string,
  ) {
    this.bot = bot;
    this.risk = risk;
    this.userId = userId;
    this.strategyId = strategyId;
    this.accountId = accountId;
  }

  // ─── Lifecycle ──────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.loadPauseState();
    void this.poll();
    this.pollTimer = setInterval(() => { void this.poll(); }, POLL_INTERVAL_MS);
    this.unsubPrice = ctraderPriceBus.subscribe((tick) => this.onTick(tick.symbolId));
  }

  stop(): void {
    this.started = false;
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    if (this.unsubPrice) { this.unsubPrice(); this.unsubPrice = null; }
  }

  onLog(fn: (l: AutoChinLogInput) => void): () => void {
    this.logListeners.add(fn);
    return () => { this.logListeners.delete(fn); };
  }

  isPaused(): boolean { return this.paused; }

  /**
   * The user edited this order from the Task Dashboard (price / volume /
   * validity). Its status was reset to `pending_new` by the edge function, so
   * drop any stale local state and re-poll at once to re-validate the new price.
   */
  notifyEdited(proposalId: string): void {
    this.settled.delete(proposalId);
    this.armed.delete(proposalId);
    void this.poll();
  }

  /** Admin pressed “Bật lại” on the dashboard → resume every pending order. */
  async resume(): Promise<void> {
    this.paused = false;
    this.pauseReason = '';
    await this.persistPause(false, '');
    this.dispatchPause(false, '');
    this.emitLog({ level: 'info', message: 'Lệnh chờ · đã BẬT LẠI — tiếp tục theo dõi thị trường' });
  }

  // ─── Poll pending orders ────────────────────────────────────

  private async poll(): Promise<void> {
    if (this.polling || !this.accountId) return;
    this.polling = true;
    try {
      const { data, error } = await supabase
        .from('telegram_order_proposals')
        .select('id, chat_id, telegram_account_id, account_id, symbol_id, symbol, trade_side, volume, status, signal_context, expires_at, error_message, created_at')
        .eq('action', 'pending_order')
        .eq('account_id', this.accountId)
        .in('status', ACTIVE_STATUSES)
        .order('created_at', { ascending: true })
        .limit(50);

      if (error || !data) return;
      const rows = data as AutoChinPendingOrder[];

      // Rebuild the armed map from the DB (authoritative).
      const nextArmed = new Map<string, AutoChinPendingOrder>();
      for (const row of rows) {
        if (row.status === 'pending_armed') nextArmed.set(row.id, row);
      }
      this.armed = nextArmed;

      // While paused, hold the whole queue — no validate/arm/fire until resumed.
      if (this.paused) return;

      for (const row of rows) {
        if (this.settled.has(row.id)) continue;
        // Expiry — validity window elapsed → cancel.
        if (row.expires_at && new Date(row.expires_at).getTime() < Date.now()) {
          await this.settle(row.id, 'cancel-pending-order', 'Hết hiệu lực lệnh chờ');
          continue;
        }
        if (row.status === 'pending_new') {
          await this.validateAndArm(row);
        }
      }
    } catch {
      /* network hiccup — retry next tick */
    } finally {
      this.polling = false;
    }
  }

  // ─── Validate the entered price vs the live market ──────────

  private async validateAndArm(row: AutoChinPendingOrder): Promise<void> {
    const symbolId = this.resolveSymbolId(row);
    if (!symbolId) return; // not tracked yet — try on the next tick
    const price = Number((row.signal_context as Record<string, unknown> | null)?.price);
    const side = String(row.trade_side || '').toUpperCase();
    if (!Number.isFinite(price) || price <= 0) return;

    const quotes = this.quotesFor(symbolId);
    if (!quotes) return; // no live quote yet — retry next tick

    const symbolName = this.symbolName(symbolId, row);

    // BUY must sit BELOW the market (buy limit); SELL must sit ABOVE it.
    if (side === 'BUY' && price >= quotes.ask) {
      const reason = `Giá BUY phải THẤP hơn giá hiện tại. Giá hiện tại: ${quotes.ask.toFixed(5)} — bạn nhập ${price}.`;
      await this.settle(row.id, 'reject-pending-order', reason);
      this.emitLog({ level: 'warn', symbolId, symbolName, message: `Lệnh chờ · ${symbolName} — ⛔ ${reason}` });
      return;
    }
    if (side === 'SELL' && price <= quotes.bid) {
      const reason = `Giá SELL phải CAO hơn giá hiện tại. Giá hiện tại: ${quotes.bid.toFixed(5)} — bạn nhập ${price}.`;
      await this.settle(row.id, 'reject-pending-order', reason);
      this.emitLog({ level: 'warn', symbolId, symbolName, message: `Lệnh chờ · ${symbolName} — ⛔ ${reason}` });
      return;
    }

    // maxOrders delivered through the conversation → Rule 2 of task5.
    const maxOrdersRaw = (row.signal_context as Record<string, unknown> | null)?.max_orders;
    const maxOrders = Number(maxOrdersRaw);
    if (Number.isFinite(maxOrders) && maxOrders > 0) {
      await this.applyMaxOrders(symbolId, symbolName, maxOrders);
    }

    try {
      const res = await telegramBot.armPendingOrder(row.id);
      if (!(res.success || res.status)) {
        return; // edge call failed — retry next tick (still pending_new)
      }
    } catch {
      return;
    }

    // Monitor it tick-by-tick immediately (don't wait for the next poll).
    this.armed.set(row.id, { ...row, status: 'pending_armed' });

    this.emitLog({
      level: 'success', symbolId, symbolName,
      message: `Lệnh chờ · ${symbolName} — giá hợp lệ, ${side} @ ${price} đã arm, đang theo dõi thị trường`,
    });
  }

  // ─── Tick-by-tick touch check ───────────────────────────────

  private onTick(symbolId: number): void {
    if (this.paused) return;
    if (this.armed.size === 0) return;
    const now = Date.now();
    if (now - this.lastTouchCheck < TOUCH_THROTTLE_MS) return;
    this.lastTouchCheck = now;

    for (const row of this.armed.values()) {
      if (this.settled.has(row.id)) continue;
      const sid = this.resolveSymbolId(row);
      if (sid !== symbolId) continue;
      void this.checkTouch(row, sid);
    }
  }

  private async checkTouch(row: AutoChinPendingOrder, symbolId: number): Promise<void> {
    const price = Number((row.signal_context as Record<string, unknown> | null)?.price);
    const side = String(row.trade_side || '').toUpperCase();
    if (!Number.isFinite(price) || price <= 0) return;

    const quotes = this.quotesFor(symbolId);
    if (!quotes) return;

    // BUY: market must FALL to the target (ask ≤ price). SELL: rise (bid ≥ price).
    const touched = side === 'BUY' ? quotes.ask <= price : quotes.bid >= price;
    if (!touched) return;

    const symbolName = this.symbolName(symbolId, row);

    // ── task5 RISK GATE before firing ──
    const gate = this.risk.canPlaceOrder(symbolId);
    if (!gate.allowed) {
      // Transient "positions not loaded yet" → don't pause the whole queue,
      // just retry on the next tick (the price is still touched).
      if (gate.blockedBy === 'notReady') return;
      this.emitLog({
        level: 'warn', symbolId, symbolName,
        message: `Lệnh chờ · ${symbolName} — giá đã chạm ${price} nhưng task5 chặn (${gate.reason}) → TẠM DỪNG toàn bộ lệnh chờ`,
      });
      await this.pauseAll(row.id, gate.reason);
      return;
    }

    this.settled.add(row.id);
    this.armed.delete(row.id);
    try {
      const res = await telegramBot.firePendingOrder(row.id);
      if (res.success) {
        this.emitLog({
          level: 'success', symbolId, symbolName,
          message: `Lệnh chờ · ${symbolName} — giá chạm ${price}, rủi ro OK, ĐÃ gửi lệnh ${side} tới cTrader`,
        });
      } else {
        this.emitLog({
          level: 'error', symbolId, symbolName,
          message: `Lệnh chờ · ${symbolName} — giá đã chạm nhưng gửi lệnh thất bại: ${res.error || 'unknown'}`,
        });
      }
    } catch (err) {
      this.emitLog({ level: 'error', symbolId, symbolName, message: `Lệnh chờ · ${symbolName} — lỗi gửi lệnh: ${String(err)}` });
    }
  }

  // ─── Pause / resume ─────────────────────────────────────────

  private async pauseAll(proposalId: string, reason: string): Promise<void> {
    this.paused = true;
    this.pauseReason = reason;
    await this.persistPause(true, reason);
    this.dispatchPause(true, reason);
    try {
      await telegramBot.pausePendingAlert(proposalId, reason);
    } catch {
      /* best-effort alert */
    }
  }

  private async loadPauseState(): Promise<void> {
    if (this.pauseLoaded || !this.userId || !this.accountId) return;
    this.pauseLoaded = true;
    try {
      const key = buildSessionKey(this.strategyId, this.accountId);
      const { data } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', this.userId)
        .eq('strategy_id', key)
        .maybeSingle();
      const sd = (data?.session_data as Record<string, unknown>) || {};
      const ws = (sd.workspace as Record<string, unknown>) || {};
      this.paused = ws.pendingOrdersPaused === true;
      this.pauseReason = typeof ws.pendingOrdersPauseReason === 'string' ? ws.pendingOrdersPauseReason : '';
      this.dispatchPause(this.paused, this.pauseReason);
    } catch {
      /* ignore */
    }
  }

  private async persistPause(paused: boolean, reason: string): Promise<void> {
    try {
      const key = buildSessionKey(this.strategyId, this.accountId);
      const { data } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', this.userId)
        .eq('strategy_id', key)
        .maybeSingle();

      const sd = (data?.session_data as Record<string, unknown>) || {};
      const ws = (sd.workspace as Record<string, unknown>) || {};
      const nextSd = {
        ...sd,
        type: 'autochinbot',
        workspace: { ...ws, pendingOrdersPaused: paused, pendingOrdersPauseReason: reason },
      };

      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: this.userId,
          strategy_id: key,
          session_data: nextSd as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });
    } catch {
      /* best-effort persistence */
    }
  }

  private dispatchPause(paused: boolean, reason: string): void {
    try {
      window.dispatchEvent(new CustomEvent(AUTOCHIN_PENDING_PAUSE_EVENT, {
        detail: { strategyId: this.strategyId, accountId: this.accountId, paused, reason },
      }));
    } catch { /* ignore */ }
  }

  // ─── Persist maxOrders → Rule 2 (config + monitor) ──────────

  private async applyMaxOrders(symbolId: number, symbolName: string, maxOrders: number): Promise<void> {
    try { this.risk.setMaxOrders(symbolId, maxOrders); } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent('autochin:maxorders', {
        detail: { strategyId: this.strategyId, accountId: this.accountId, symbolId, maxOrders },
      }));
    } catch { /* ignore */ }
    try {
      const key = buildSessionKey(this.strategyId, this.accountId);
      const { data } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', this.userId)
        .eq('strategy_id', key)
        .maybeSingle();

      const sd = (data?.session_data as Record<string, unknown>) || {};
      const ws = (sd.workspace as Record<string, unknown>) || {};
      const cfgs = { ...((ws.symbolConfigs as Record<string, unknown>) || {}) };
      const current = (cfgs[String(symbolId)] as Record<string, unknown>) || {};
      cfgs[String(symbolId)] = { ...current, maxOrders };

      const nextSd = { ...sd, type: 'autochinbot', workspace: { ...ws, symbolConfigs: cfgs } };
      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: this.userId,
          strategy_id: key,
          session_data: nextSd as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });

      this.emitLog({ level: 'info', symbolId, symbolName, message: `Lệnh chờ · đã lưu maxOrders=${maxOrders} cho ${symbolName}` });
    } catch {
      /* best-effort */
    }
  }

  // ─── Helpers ────────────────────────────────────────────────

  private async settle(proposalId: string, action: 'reject-pending-order' | 'cancel-pending-order', reason: string): Promise<void> {
    this.settled.add(proposalId);
    this.armed.delete(proposalId);
    try {
      if (action === 'reject-pending-order') await telegramBot.rejectPendingOrder(proposalId, reason);
      else await telegramBot.cancelPendingOrder(proposalId, reason);
    } catch { /* best-effort */ }
  }

  private resolveSymbolId(row: AutoChinPendingOrder): number | null {
    const wanted = String(row.symbol || '').trim().toUpperCase();
    const snap = this.bot.snapshot();
    if (wanted) {
      for (const idStr of Object.keys(snap.symbols)) {
        const id = Number(idStr);
        const buf = snap.symbols[id];
        if (buf && buf.symbolName.toUpperCase() === wanted) return id;
      }
    }
    if (row.symbol_id && snap.symbols[row.symbol_id]) return row.symbol_id;
    return row.symbol_id ?? null;
  }

  private symbolName(symbolId: number, row: AutoChinPendingOrder): string {
    return this.bot.snapshot().symbols[symbolId]?.symbolName || row.symbol || `#${symbolId}`;
  }

  /** Account-scoped live quotes read from this engine's own buffers. */
  private quotesFor(symbolId: number): { bid: number; ask: number } | null {
    const buf = this.bot.snapshot().symbols[symbolId];
    if (!buf || buf.currentBid == null || buf.currentAsk == null) return null;
    if (buf.currentBid <= 0 || buf.currentAsk <= 0) return null;
    return { bid: buf.currentBid, ask: buf.currentAsk };
  }

  // ─── Emit ───────────────────────────────────────────────────

  private emitLog(input: AutoChinLogInput): void {
    this.logListeners.forEach((fn) => {
      try { fn(input); } catch { /* ignore */ }
    });
  }
}